// client/src/components/Post/ReaderModerationBanner/index.tsx
import * as React from 'react';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router';
import { PRIVATE_URIS } from '../../../constants/privateURIs.constants';
import { useSocket } from '../../../context/WebsocketContext';
import { resolveNavRoute } from '../../../helpers/resolveNavRoute';
import type { UserState } from '../../../interfaces/user.interface';
import {
  initialFor,
  type ModerationStatusEnum,
  type ReaderBannerState,
  transition,
} from './types';
import './reader-moderation-banner.scss';

interface PostStatusChangedPayload {
  postId: string;
  status: ModerationStatusEnum;
  authorId: string;
}

interface Props {
  postId: string;
  initialStatus: ModerationStatusEnum;
  onSwapToRevised: () => void;
}

const COUNTDOWN_SECONDS = 5;

/**
 * Banner shown to non-author readers when a post they're viewing is in
 * a moderation state. Subscribes to post_status_changed events and runs
 * a state machine across pending_rewrite / revised / removed states.
 *
 * Cold-load (initialStatus !== PUBLISHED) and live transitions both
 * converge on the same render code.
 */
export const ReaderModerationBanner = ({
  postId,
  initialStatus,
  onSwapToRevised,
}: Props): React.ReactElement | null => {
  const [state, setState] = React.useState<ReaderBannerState>(
    initialFor(initialStatus),
  );
  const prevStatusRef = React.useRef<ModerationStatusEnum>(initialStatus);
  const { onEvent } = useSocket();

  React.useEffect(() => {
    const off = onEvent(
      'post_status_changed',
      (payload: PostStatusChangedPayload) => {
        if (payload.postId !== postId) return;
        const next = transition(prevStatusRef.current, payload.status);
        prevStatusRef.current = payload.status;
        setState(next);
      },
    );
    return off;
  }, [postId, onEvent]);

  switch (state.kind) {
    case 'none':
      return null;
    case 'pending_rewrite':
      return <PendingRewriteBanner />;
    case 'revised':
      return <RevisedBanner onSwap={onSwapToRevised} />;
    case 'removed':
      return <RemovedBanner />;
  }
};

// --- Sub-banners (internal) ---

const PendingRewriteBanner = (): React.ReactElement => (
  <div className="reader-banner reader-banner--pending" role="status">
    <div className="reader-banner__body">
      <strong>This post is being reviewed.</strong>
      <span className="reader-banner__sub">
        The author may publish a revised version shortly.
      </span>
    </div>
  </div>
);

// Matches `.reader-banner--fading` transition duration in
// reader-moderation-banner.scss, plus a small safety margin in case
// transitionend doesn't fire (prefers-reduced-motion sets transition: none,
// which leaves no event for some browsers to dispatch).
const FADE_OUT_FALLBACK_MS = 2100;

const RevisedBanner = ({
  onSwap,
}: {
  onSwap: () => void;
}): React.ReactElement | null => {
  const [secondsLeft, setSecondsLeft] = React.useState(COUNTDOWN_SECONDS);
  const [hasSwapped, setHasSwapped] = React.useState(false);
  const [isHidden, setIsHidden] = React.useState(false);

  React.useEffect(() => {
    if (hasSwapped) return;
    if (secondsLeft <= 0) {
      onSwap();
      setHasSwapped(true);
      return;
    }
    const t = setTimeout(() => setSecondsLeft((s) => s - 1), 1000);
    return () => clearTimeout(t);
  }, [secondsLeft, hasSwapped, onSwap]);

  // TRIL-105: unmount the banner once the fade-out finishes so the
  // post-detail layout reclaims the space. Without this, the element
  // stays opacity:0 in the DOM and leaves a visible gap.
  React.useEffect(() => {
    if (!hasSwapped) return;
    const t = setTimeout(() => setIsHidden(true), FADE_OUT_FALLBACK_MS);
    return () => clearTimeout(t);
  }, [hasSwapped]);

  const handleTransitionEnd = (e: React.TransitionEvent<HTMLDivElement>) => {
    if (hasSwapped && e.propertyName === 'opacity') {
      setIsHidden(true);
    }
  };

  const handleRefreshNow = () => {
    if (hasSwapped) return;
    onSwap();
    setHasSwapped(true);
  };

  if (isHidden) return null;

  return (
    <div
      className={`reader-banner reader-banner--revised${hasSwapped ? ' reader-banner--fading' : ''}`}
      role="status"
      onTransitionEnd={handleTransitionEnd}
    >
      <div className="reader-banner__body">
        <strong>Updated.</strong>{' '}
        <span>A revised version is now available.</span>
        {!hasSwapped && (
          <span className="reader-banner__countdown">
            <span className="reader-banner__spinner" aria-hidden="true" />
            <span>Auto-refreshing in {secondsLeft}s…</span>
          </span>
        )}
      </div>
      {!hasSwapped && (
        <button
          type="button"
          className="reader-banner__action"
          onClick={handleRefreshNow}
        >
          Refresh now
        </button>
      )}
    </div>
  );
};

const RemovedBanner = (): React.ReactElement => {
  const navigate = useNavigate();
  const { user } = useSelector((state: UserState) => state.userStore);
  // TRIL-189: redirect to the canonical feed route, not the bare '/feed'
  // literal (which isn't a registered route — see TRIL-187 review).
  const feedRoute = resolveNavRoute(PRIVATE_URIS.FEED, user?.username);
  const [secondsLeft, setSecondsLeft] = React.useState(COUNTDOWN_SECONDS);

  React.useEffect(() => {
    if (secondsLeft <= 0) {
      navigate(feedRoute);
      return;
    }
    const t = setTimeout(() => setSecondsLeft((s) => s - 1), 1000);
    return () => clearTimeout(t);
  }, [secondsLeft, navigate, feedRoute]);

  const handleGoNow = () => navigate(feedRoute);

  return (
    <div className="reader-banner reader-banner--removed" role="status">
      <div className="reader-banner__body">
        <strong>This post has been removed.</strong>
        <span className="reader-banner__countdown">
          <span
            className="reader-banner__spinner reader-banner__spinner--rose"
            aria-hidden="true"
          />
          <span>Returning to feed in {secondsLeft}s…</span>
        </span>
      </div>
      <button
        type="button"
        className="reader-banner__action reader-banner__action--rose"
        onClick={handleGoNow}
        aria-label="Return to feed now"
      >
        Go now
      </button>
    </div>
  );
};
